// packages/core/src/components/schedule-v2/ScheduleEditorV2.tsx
//
// Composant racine de l'éditeur d'horaires V2, derrière le feature flag
// `enableScheduleEditorV2` (FeatureFlagsContext, D34e). Remplace
// `TimeSlotsTable` dans PollCreator.tsx quand le flag est actif — fichier
// séparé, V1 intacte (cf. Docs/SPECS/2026-09-12-editeur-horaires-v2-revue-
// critique.md §6 : "V2 en fichiers séparés").
//
// Frontière avec PollCreator (D34b, simplifié) : ce composant possède son
// PROPRE état interne (`dayRanges`, modèle `Range[]` par date) ; il ne lit
// `initialTimeSlotsByDate` qu'à l'HYDRATATION (une fois, quand des données
// arrivent pour la première fois — sondage en édition chargé de façon
// asynchrone, ou proposition IA initiale), puis remonte ses changements au
// format V1 via `onChange` (debounce 300 ms, D22 — PollCreator relaie
// ensuite vers `EditorStateProvider`, cf. son propre fichier). Une NOUVELLE
// proposition IA arrivée APRÈS l'hydratation arrive par la prop distincte
// `aiTimeSlotsByDate` (D21) : jour jamais retouché → adoption silencieuse ;
// jour retouché → badge "L'IA propose... — appliquer ?".
//
// Ce que ce composant implémente : D1, D3, D4, D6, D8, D8b, D9, D11, D12,
// D13 (glisser diagonal multi-colonnes), D14 (déplacer en tirant le corps,
// souris seulement — la logique de geste vit dans usePaintGesture.ts),
// D16 (annuler/rétablir multi-niveaux, Ctrl+Z/Ctrl+Y + boutons), D17 (base :
// Tab + nom parlé + Suppr + flèches + Entrée ouvre la saisie exacte +
// création au clavier via la saisie exacte — grille ARIA complète : plus
// tard), D18-D20 (recopie simplifiée), D21 (badge IA par jour), D22 (sync
// 300 ms, ce composant → PollCreator → EditorStateProvider), D26 (saisie
// exacte : popover desktop, bottom sheet mobile, deux champs `type="time"`),
// D32, D33 (grille, dont D33o : ancrage du défilement au changement de pas),
// D34a/D34e/D34f/D34g/D34h.
// Explicitement DIFFÉRÉ (documenté dans le rapport de tests, pas dans ce
// fichier pour ne pas alourdir la lecture) : D33 ARIA grille complète.
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  Scissors,
  Link2,
  Trash2,
  CopyPlus,
  Undo2,
  Redo2,
  Plus,
} from "lucide-react";
import {
  dayFromStorage,
  toStorage,
  type EmittedSlot,
} from "../../schedule/storage";
import {
  isValidRange,
  mergeWithAdjacent,
  moveRangeBy,
  overlapsAny,
  removeRangeAt,
  splitRangeAt,
  tryAddRange,
} from "../../schedule/intervals";
import { realignForStep } from "../../schedule/step";
import {
  STEP_VALUES,
  DAY_MINUTES,
  type DayRangesMap,
  type Range,
  type Step,
  type StoredTimeSlotsByDate,
} from "../../schedule/types";
import { ScheduleGridV2, type ScheduleGridV2Handle } from "./ScheduleGridV2";
import type {
  MultiColumnPaintResult,
  ScheduleSelection,
} from "./usePaintGesture";
import { localeTag } from "@core/i18n";

const NARROW_BREAKPOINT_PX = 640; // D24
const DEBOUNCE_MS = 300; // esprit D22, en local (pas de PATCH_DRAFT vers EditorStateProvider ici)
const DEFAULT_VISIBLE_START = 8 * 60;
const DEFAULT_VISIBLE_END = 20 * 60;
const MAX_HISTORY = 50; // D16 : multi-niveaux, mais borné pour éviter une session infinie en mémoire

/** "540" → "09:00" pour un `<input type="time">` (D26). 1440 (24:00 exact) n'a pas de représentation native — affiché "00:00", limite connue et acceptée. */
function minutesToTimeInput(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** "09:00" → 540, ou null si le format ne correspond pas à un `<input type="time">`. */
function parseTimeInput(value: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

interface HistorySnapshot {
  dayRanges: DayRangesMap;
  step: Step;
}

interface ExactEntryState {
  mode: "create" | "edit";
  date: string;
  /** Index de la plage éditée ; absent en mode création. */
  index?: number;
  start: number;
  end: number;
}

/** Jour de semaine en UTC, indépendant du fuseau du navigateur (D34h). 1=lundi..7=dimanche, 6/7=week-end. */
function weekdayOfUTC(dateStr: string): number {
  const d = new Date(dateStr + "T00:00:00Z");
  const jsDay = d.getUTCDay(); // 0=dimanche
  return jsDay === 0 ? 7 : jsDay;
}

/** "09:00-10:30" — pour le libellé du badge IA (D21), pas pour le rendu de la grille. */
function formatRangeShort(r: Range): string {
  const fmt = (m: number) =>
    `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  return `${fmt(r.start)}-${fmt(r.end)}`;
}

function rangesEqual(a: Range[], b: Range[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x.start - y.start);
  const sb = [...b].sort((x, y) => x.start - y.start);
  return sa.every((r, i) => r.start === sb[i].start && r.end === sb[i].end);
}

export interface ScheduleEditorV2Props {
  selectedDates: string[];
  initialTimeSlotsByDate: StoredTimeSlotsByDate;
  initialGranularity: number;
  initialMergeMode: boolean;
  onChange: (
    timeSlotsByDate: Record<string, EmittedSlot[]>,
    granularity: number,
  ) => void;
  /**
   * D21/D22 : dernière proposition IA reçue APRÈS l'hydratation initiale
   * (ex. le chat a répondu pendant que l'utilisateur édite déjà). Un jour
   * jamais retouché par l'utilisateur adopte cette proposition
   * silencieusement ; un jour retouché garde sa version manuelle et
   * affiche un badge "L'IA propose ... — appliquer ?" au lieu d'écraser.
   * `undefined` = pas de nouvelle proposition ; un objet {} vide compte
   * comme une proposition (l'IA n'a plus de créneaux à proposer).
   */
  aiTimeSlotsByDate?: StoredTimeSlotsByDate;
  aiGranularity?: number;
  aiMergeMode?: boolean;
  /** Sondage avec réponses déjà reçues : grille figée (D34h). */
  readOnly?: boolean;
  /** Horloge injectable pour les tests des heures passées (D32, D34h). */
  now?: Date;
  className?: string;
}

export const ScheduleEditorV2: React.FC<ScheduleEditorV2Props> = ({
  selectedDates,
  initialTimeSlotsByDate,
  initialGranularity,
  initialMergeMode,
  onChange,
  aiTimeSlotsByDate,
  aiGranularity,
  aiMergeMode,
  readOnly = false,
  now,
  className,
}) => {
  const { t, i18n } = useTranslation(["creator", "common"]);
  const gridRef = useRef<ScheduleGridV2Handle>(null);
  const stepConfirmRef = useRef<HTMLDivElement>(null);
  const applyAllConfirmRef = useRef<HTMLDivElement>(null);
  const dayFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(localeTag(i18n.language), {
        weekday: "short",
        day: "numeric",
      }),
    [i18n.language],
  );

  const [step, setStep] = useState<Step>(
    (STEP_VALUES as readonly number[]).includes(initialGranularity)
      ? (initialGranularity as Step)
      : 30,
  );
  const [dayRanges, setDayRanges] = useState<DayRangesMap>({});
  const [visibleStart, setVisibleStart] = useState(DEFAULT_VISIBLE_START);
  const [visibleEnd, setVisibleEnd] = useState(DEFAULT_VISIBLE_END);
  const [selected, setSelected] = useState<ScheduleSelection | null>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [splitMode, setSplitMode] = useState(false);
  const [pendingStep, setPendingStep] = useState<Step | null>(null);
  const [applyAllConfirm, setApplyAllConfirm] = useState<{
    target: "all" | "weekdays";
    affected: string[];
  } | null>(null);
  // D13 : message affiché au relâchement d'un glisser multi-colonnes qui a
  // sauté au moins un jour (chevauchement d'une plage existante).
  const [skippedDaysNotice, setSkippedDaysNotice] = useState<string[] | null>(
    null,
  );
  const skippedDaysTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // D16 : annuler/rétablir multi-niveaux sur toute la session d'édition.
  const [past, setPast] = useState<HistorySnapshot[]>([]);
  const [future, setFuture] = useState<HistorySnapshot[]>([]);
  // D17/D26 : saisie exacte (Entrée sur une plage sélectionnée = édition ;
  // bouton "Ajouter un créneau" = création au clavier).
  const [exactEntry, setExactEntry] = useState<ExactEntryState | null>(null);
  const [exactEntryError, setExactEntryError] = useState<string | null>(null);
  // D21 : jours que l'utilisateur a modifiés à la main au moins une fois
  // cette session — protégés d'un écrasement silencieux par une future
  // proposition IA (ils reçoivent un badge à la place). Ne redevient jamais
  // "non retouché" (même après Annuler) : plus sûr par défaut.
  const [touchedDates, setTouchedDates] = useState<Set<string>>(new Set());
  // D21 : propositions IA en attente pour un jour retouché (badge).
  const [pendingAiProposals, setPendingAiProposals] = useState<DayRangesMap>(
    {},
  );

  const hasHydratedRef = useRef(false);
  const changeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hydratation UNIQUE (D34b, simplifié) : la première fois que des données
  // externes existent (dates sélectionnées et/ou créneaux déjà en base),
  // on initialise l'état interne. Les mises à jour externes suivantes
  // (ex. l'IA modifie le sondage après coup) ne sont PAS répercutées ici —
  // c'est le comportement D21/D22 non implémenté dans cette itération.
  useEffect(() => {
    if (hasHydratedRef.current) return;
    if (selectedDates.length === 0) return;
    hasHydratedRef.current = true;
    const initial: DayRangesMap = {};
    for (const date of selectedDates) {
      initial[date] = dayFromStorage(
        initialTimeSlotsByDate[date],
        initialGranularity > 0 ? initialGranularity : 30,
        initialMergeMode,
      );
    }
    setDayRanges(initial);
  }, [
    selectedDates,
    initialTimeSlotsByDate,
    initialGranularity,
    initialMergeMode,
  ]);

  // D20 : une nouvelle date cochée hérite automatiquement si toutes les
  // dates existantes ont le même horaire ; sinon elle arrive vide. Ne
  // touche jamais une date déjà connue (pas de réinitialisation surprise).
  useEffect(() => {
    if (!hasHydratedRef.current) return;
    // Le calcul de "missing" se fait DANS le updater fonctionnel, jamais à
    // partir de la fermeture de l'effet : sur le tout premier rendu, cet
    // effet et celui d'hydratation se déclenchent dans la même passe, et la
    // fermeture verrait encore `dayRanges = {}` alors que l'hydratation vient
    // juste d'être mise en file — lire `prev` évite la course.
    setDayRanges((prev) => {
      const missing = selectedDates.filter((d) => !(d in prev));
      if (missing.length === 0) return prev;
      const known = Object.keys(prev);
      const allSame =
        known.length > 0 &&
        known.every((d) => rangesEqual(prev[d], prev[known[0]]));
      const inherited = allSame ? prev[known[0]] : [];
      const next = { ...prev };
      for (const date of missing) {
        next[date] = inherited.map((r) => ({ ...r }));
      }
      return next;
    });
  }, [selectedDates]);

  // D9 : étendre automatiquement la fenêtre visible dès qu'une plage en sort.
  useEffect(() => {
    let min = visibleStart;
    let max = visibleEnd;
    for (const ranges of Object.values(dayRanges)) {
      for (const r of ranges) {
        if (r.start < min) min = Math.floor(r.start / 60) * 60;
        if (r.end > max) max = Math.ceil(r.end / 60) * 60;
      }
    }
    if (min !== visibleStart) setVisibleStart(min);
    if (max !== visibleEnd) setVisibleEnd(max);
  }, [dayRanges]);

  // Remontée débouncée vers PollCreator (D22 : celui-ci la relaie ensuite
  // vers EditorStateProvider — cf. son propre `onChange` — pour que le chat
  // raisonne sur ce que l'utilisateur voit).
  useEffect(() => {
    if (!hasHydratedRef.current) return;
    if (changeDebounceRef.current) clearTimeout(changeDebounceRef.current);
    changeDebounceRef.current = setTimeout(() => {
      onChange(toStorage(dayRanges, selectedDates), step);
    }, DEBOUNCE_MS);
    return () => {
      if (changeDebounceRef.current) clearTimeout(changeDebounceRef.current);
    };
  }, [dayRanges, step, selectedDates]);

  // D24 : sous 640px, un seul jour à la fois.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${NARROW_BREAKPOINT_PX - 1}px)`);
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (mobileIndex >= selectedDates.length) setMobileIndex(0);
  }, [selectedDates, mobileIndex]);

  const datesToRender = useMemo(
    () =>
      isNarrow
        ? selectedDates.slice(mobileIndex, mobileIndex + 1)
        : selectedDates,
    [isNarrow, selectedDates, mobileIndex],
  );

  // D33o : ancrage du défilement au changement de pas. L'heure au centre de
  // la zone visible est lue DANS le gestionnaire, avant le changement
  // d'état — après le rendu, la grille a déjà rétréci et le navigateur a pu
  // borner `scrollTop`, la position d'origine serait perdue. Elle est
  // réappliquée en `useLayoutEffect`, avant l'affichage : pas de saut
  // visible. Tout changement de pas passe par `changeStep` (bouton,
  // dialogue D8b, annuler/rétablir D16).
  const stepAnchorRef = useRef<number | null>(null);
  const changeStep = useCallback(
    (nextStep: Step) => {
      if (nextStep === step) return;
      stepAnchorRef.current = gridRef.current?.getCenterMinute() ?? null;
      setStep(nextStep);
    },
    [step],
  );
  useLayoutEffect(() => {
    const anchor = stepAnchorRef.current;
    stepAnchorRef.current = null;
    if (anchor !== null) gridRef.current?.scrollToMinutes(anchor, "instant");
  }, [step]);

  // D16 : point de passage UNIQUE pour toute modification de fond
  // (`dayRanges` et/ou `step`) déclenchée par une action utilisateur —
  // empile l'état PRÉCÉDENT avant d'appliquer le nouveau, vide la pile
  // "rétablir". Les mises à jour purement automatiques (hydratation,
  // héritage D20, fenêtre visible D9) passent par `setDayRanges`
  // directement et NE sont PAS undoable, volontairement : ce ne sont pas
  // des gestes de l'utilisateur.
  const commitDayRanges = useCallback(
    (updater: (prev: DayRangesMap) => DayRangesMap, nextStep?: Step) => {
      setPast((p) => {
        const next = [...p, { dayRanges, step }];
        return next.length > MAX_HISTORY
          ? next.slice(next.length - MAX_HISTORY)
          : next;
      });
      setFuture([]);
      setDayRanges(updater);
      if (nextStep !== undefined) changeStep(nextStep);
    },
    [dayRanges, step, changeStep],
  );

  // D21 : marque un ou plusieurs jours comme retouchés par l'utilisateur.
  // Ne redevient jamais "non retouché" — même par Annuler (D16) — plus sûr
  // par défaut qu'un jour protégé qui se remette à accepter des mises à
  // jour IA silencieuses sans que l'utilisateur l'ait décidé.
  const markTouched = useCallback((dates: string[]) => {
    setTouchedDates((prev) => {
      if (dates.every((d) => prev.has(d))) return prev;
      const next = new Set(prev);
      for (const d of dates) next.add(d);
      return next;
    });
  }, []);

  const setDayRangesFor = useCallback(
    (date: string, ranges: Range[]) => {
      commitDayRanges((prev) => ({ ...prev, [date]: ranges }));
      markTouched([date]);
    },
    [commitDayRanges, markTouched],
  );

  // D21/D22 : une NOUVELLE proposition IA arrivée après l'hydratation
  // initiale (le chat a répondu pendant que l'utilisateur édite déjà). Un
  // jour jamais retouché adopte silencieusement (via `commitDayRanges`,
  // donc annulable comme tout le reste, D16) ; un jour retouché reçoit un
  // badge au lieu d'être écrasé.
  const aiSignature = useMemo(
    () =>
      aiTimeSlotsByDate !== undefined
        ? JSON.stringify(aiTimeSlotsByDate)
        : undefined,
    [aiTimeSlotsByDate],
  );
  // `undefined` initial : la toute première proposition définie (qu'elle
  // arrive au montage ou bien plus tard) est traitée normalement — le
  // diff par jour ci-dessous (`rangesEqual`) absorbe déjà sans effet de
  // bord le cas où elle coïncide avec l'hydratation initiale.
  const lastAiSignatureRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!hasHydratedRef.current) return;
    if (aiSignature === undefined) return;
    if (aiSignature === lastAiSignatureRef.current) return;
    lastAiSignatureRef.current = aiSignature;

    const g = aiGranularity && aiGranularity > 0 ? aiGranularity : step;
    const silentChanges: DayRangesMap = {};
    const badgeChanges: DayRangesMap = {};
    for (const date of selectedDates) {
      if (!aiTimeSlotsByDate || !(date in aiTimeSlotsByDate)) continue;
      const proposed = dayFromStorage(
        aiTimeSlotsByDate[date],
        g,
        aiMergeMode ?? false,
      );
      const current = dayRanges[date] ?? [];
      if (rangesEqual(proposed, current)) continue;
      if (touchedDates.has(date)) {
        badgeChanges[date] = proposed;
      } else {
        silentChanges[date] = proposed;
      }
    }
    if (Object.keys(silentChanges).length > 0) {
      commitDayRanges((prev) => ({ ...prev, ...silentChanges }));
    }
    if (Object.keys(badgeChanges).length > 0) {
      setPendingAiProposals((prev) => ({ ...prev, ...badgeChanges }));
    }
    // dayRanges/touchedDates/commitDayRanges/step/selectedDates/
    // aiGranularity/aiMergeMode/aiTimeSlotsByDate volontairement absents
    // des deps : cet effet doit se déclencher UNIQUEMENT sur une vraie
    // nouvelle proposition (`aiSignature`), en lisant toujours la valeur
    // la plus récente de tout le reste via la fermeture du rendu courant.
  }, [aiSignature]);

  const applyAiProposal = useCallback(
    (date: string) => {
      const proposed = pendingAiProposals[date];
      if (!proposed) return;
      commitDayRanges((prev) => ({ ...prev, [date]: proposed }));
      setPendingAiProposals((prev) => {
        const next = { ...prev };
        delete next[date];
        return next;
      });
    },
    [pendingAiProposals, commitDayRanges],
  );

  const dismissAiProposal = useCallback((date: string) => {
    setPendingAiProposals((prev) => {
      const next = { ...prev };
      delete next[date];
      return next;
    });
  }, []);

  const applyAllAiProposals = useCallback(() => {
    const entries = Object.entries(pendingAiProposals);
    if (entries.length === 0) return;
    commitDayRanges((prev) => {
      const next = { ...prev };
      for (const [date, ranges] of entries) next[date] = ranges;
      return next;
    });
    setPendingAiProposals({});
  }, [pendingAiProposals, commitDayRanges]);

  const undo = useCallback(() => {
    if (past.length === 0) return;
    const previous = past[past.length - 1];
    setPast((p) => p.slice(0, -1));
    setFuture((f) => [{ dayRanges, step }, ...f]);
    setDayRanges(previous.dayRanges);
    changeStep(previous.step);
    setSelected(null);
    setSplitMode(false);
  }, [past, dayRanges, step, changeStep]);

  const redo = useCallback(() => {
    if (future.length === 0) return;
    const next = future[0];
    setFuture((f) => f.slice(1));
    setPast((p) => [...p, { dayRanges, step }]);
    setDayRanges(next.dayRanges);
    changeStep(next.step);
    setSelected(null);
    setSplitMode(false);
  }, [future, dayRanges, step, changeStep]);

  const requestStepChange = useCallback(
    (newStep: Step) => {
      if (newStep === step) return;
      const { changed } = realignForStep(dayRanges, newStep);
      if (!changed) {
        commitDayRanges((prev) => prev, newStep);
        return;
      }
      setPendingStep(newStep);
    },
    [dayRanges, step, commitDayRanges],
  );

  const applyPendingStep = useCallback(
    (mode: "realign" | "keep" | "display-only") => {
      if (pendingStep === null) return;
      if (mode === "realign") {
        const { ranges } = realignForStep(dayRanges, pendingStep);
        commitDayRanges(() => ranges, pendingStep);
        // D21 : "Réaligner" modifie les plages par un choix explicite de
        // l'utilisateur — sans ceci, une proposition IA reçue juste après
        // écraserait silencieusement ce choix (le seul gate est
        // `touchedDates`, alimenté nulle part ailleurs pour ce chemin).
        markTouched(Object.keys(ranges));
      } else if (mode === "display-only") {
        // La grille se redessine ; les plages restent "hors grille" (D8b).
        commitDayRanges((prev) => prev, pendingStep);
      }
      // "keep" : on ne change rien, ni les plages ni le pas — rien à empiler.
      setPendingStep(null);
    },
    [pendingStep, dayRanges, commitDayRanges, markTouched],
  );

  const todayCutoff = useMemo(() => {
    const clock = now ?? new Date();
    const todayIso = `${clock.getFullYear()}-${String(clock.getMonth() + 1).padStart(2, "0")}-${String(
      clock.getDate(),
    ).padStart(2, "0")}`;
    if (!selectedDates.includes(todayIso)) return null;
    return {
      date: todayIso,
      minute: clock.getHours() * 60 + clock.getMinutes(),
    };
  }, [now, selectedDates]);

  const selectedRange =
    selected && dayRanges[selected.date]
      ? dayRanges[selected.date][selected.index]
      : null;

  const handleDeleteSelected = useCallback(() => {
    if (!selected) return;
    setDayRangesFor(
      selected.date,
      removeRangeAt(dayRanges[selected.date] ?? [], selected.index),
    );
    setSelected(null);
    setSplitMode(false);
  }, [selected, dayRanges, setDayRangesFor]);

  const handleMerge = useCallback(() => {
    if (!selected) return;
    const ranges = dayRanges[selected.date] ?? [];
    const result = mergeWithAdjacent(ranges, selected.index);
    if (!result.ok) return;
    // Retire UNIQUEMENT la plage sélectionnée et la voisine EFFECTIVEMENT
    // fusionnée (`neighborIndex`) — jamais toute plage qui touche un bord
    // de la cible : avec trois plages bord-à-bord, ça effacerait la 3ᵉ
    // (de l'autre côté) au lieu de la laisser intacte (bug corrigé).
    const rest = ranges.filter(
      (_, i) => i !== selected.index && i !== result.neighborIndex,
    );
    setDayRangesFor(selected.date, [...rest, result.range]);
    setSelected(null);
  }, [selected, dayRanges, setDayRangesFor]);

  const handleSplitAt = useCallback(
    (atMinute: number) => {
      if (!selected) return;
      const result = splitRangeAt(
        dayRanges[selected.date] ?? [],
        selected.index,
        atMinute,
      );
      if (!result.ok) return;
      const rest = removeRangeAt(
        dayRanges[selected.date] ?? [],
        selected.index,
      );
      setDayRangesFor(selected.date, [...rest, result.first, result.second]);
      setSelected(null);
      setSplitMode(false);
    },
    [selected, dayRanges, setDayRangesFor],
  );

  const handleMoveSelected = useCallback(
    (deltaSteps: number) => {
      if (!selected) return;
      const result = moveRangeBy(
        dayRanges[selected.date] ?? [],
        selected.index,
        deltaSteps * step,
      );
      if (!result.ok) return;
      const next = (dayRanges[selected.date] ?? []).slice();
      next[selected.index] = result.range;
      setDayRangesFor(selected.date, next);
    },
    [selected, dayRanges, step, setDayRangesFor],
  );

  // D17 : Entrée sur une plage sélectionnée ouvre la saisie exacte en édition.
  const openExactEntryForSelected = useCallback(() => {
    if (!selected || !selectedRange) return;
    setExactEntry({
      mode: "edit",
      date: selected.date,
      index: selected.index,
      start: selectedRange.start,
      end: selectedRange.end,
    });
    setExactEntryError(null);
  }, [selected, selectedRange]);

  // D17 : création au clavier via la saisie exacte, sans dépendre d'un geste
  // pointeur. Pré-remplit avec un créneau de la durée du pas courant, au
  // début de la fenêtre visible.
  const openExactEntryForCreate = useCallback(() => {
    const date = selectedDates[isNarrow ? mobileIndex : 0];
    if (!date) return;
    const start = Math.min(Math.max(0, visibleStart), DAY_MINUTES - step);
    setExactEntry({ mode: "create", date, start, end: start + step });
    setExactEntryError(null);
  }, [selectedDates, isNarrow, mobileIndex, visibleStart, step]);

  const submitExactEntry = useCallback(() => {
    if (!exactEntry) return;
    const candidate: Range = { start: exactEntry.start, end: exactEntry.end };
    const validity = isValidRange(candidate);
    // `"reason" in x` plutôt que `!x.ok` : le tsconfig du package a
    // `strict: false` (donc `strictNullChecks: false`), sous lequel TS ne
    // rétrécit pas de façon fiable un discriminant booléen vers sa branche
    // `false` — seul un test de présence de propriété narrowe ici.
    if ("reason" in validity) {
      setExactEntryError(validity.reason);
      return;
    }
    const existing = dayRanges[exactEntry.date] ?? [];
    if (exactEntry.mode === "create") {
      const result = tryAddRange(existing, candidate);
      if ("reason" in result) {
        setExactEntryError(result.reason);
        return;
      }
      commitDayRanges((prev) => ({
        ...prev,
        [exactEntry.date]: [...(prev[exactEntry.date] ?? []), result.range],
      }));
      setSelected({ date: exactEntry.date, index: existing.length });
    } else {
      if (overlapsAny(candidate, existing, exactEntry.index)) {
        setExactEntryError("overlap");
        return;
      }
      const next = existing.slice();
      next[exactEntry.index!] = candidate;
      commitDayRanges((prev) => ({ ...prev, [exactEntry.date]: next }));
    }
    markTouched([exactEntry.date]);
    setExactEntry(null);
    setExactEntryError(null);
  }, [exactEntry, dayRanges, commitDayRanges, markTouched]);

  const handleMultiColumnPaint = useCallback(
    (result: MultiColumnPaintResult) => {
      if (skippedDaysTimerRef.current)
        clearTimeout(skippedDaysTimerRef.current);
      if (result.skipped.length === 0) {
        setSkippedDaysNotice(null);
        return;
      }
      setSkippedDaysNotice(result.skipped);
      skippedDaysTimerRef.current = setTimeout(
        () => setSkippedDaysNotice(null),
        5000,
      );
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (skippedDaysTimerRef.current)
        clearTimeout(skippedDaysTimerRef.current);
    };
  }, []);

  const canMerge =
    !!selected &&
    mergeWithAdjacent(dayRanges[selected.date] ?? [], selected.index).ok;

  const requestApplyToAllDays = useCallback(
    (target: "all" | "weekdays") => {
      const source = selectedDates[isNarrow ? mobileIndex : 0];
      if (!source) return;
      const sourceRanges = dayRanges[source] ?? [];
      const targets = selectedDates.filter((d) => {
        if (d === source) return false;
        if (target === "weekdays" && weekdayOfUTC(d) > 5) return false;
        return true;
      });
      const affected = targets.filter(
        (d) => !rangesEqual(dayRanges[d] ?? [], sourceRanges),
      );
      if (affected.length === 0) {
        // Rien ne change réellement (tous les jours cibles ont déjà les
        // mêmes plages que la source) : ne PAS empiler d'entrée d'historique
        // vide — un premier Annuler qui "ne fait rien" est déroutant.
        if (targets.length > 0) markTouched(targets);
        return;
      }
      setApplyAllConfirm({ target, affected });
    },
    [
      dayRanges,
      selectedDates,
      isNarrow,
      mobileIndex,
      commitDayRanges,
      markTouched,
    ],
  );

  const confirmApplyToAllDays = useCallback(() => {
    if (!applyAllConfirm) return;
    const source = selectedDates[isNarrow ? mobileIndex : 0];
    const sourceRanges = dayRanges[source] ?? [];
    const targets = selectedDates.filter((d) => {
      if (d === source) return false;
      if (applyAllConfirm.target === "weekdays" && weekdayOfUTC(d) > 5)
        return false;
      return true;
    });
    commitDayRanges((prev) => {
      const next = { ...prev };
      for (const d of targets) next[d] = sourceRanges.map((r) => ({ ...r }));
      return next;
    });
    markTouched(targets);
    setApplyAllConfirm(null);
  }, [
    applyAllConfirm,
    dayRanges,
    selectedDates,
    isNarrow,
    mobileIndex,
    commitDayRanges,
    markTouched,
  ]);

  // a11y de base (D17) : Ctrl+Z/Ctrl+Y (D16) globaux à la session d'édition,
  // indépendants de la sélection ; Suppr/flèches/Entrée agissent sur la
  // plage sélectionnée.
  useEffect(() => {
    if (readOnly) return;
    function onKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (
        mod &&
        (e.key.toLowerCase() === "y" ||
          (e.key.toLowerCase() === "z" && e.shiftKey))
      ) {
        e.preventDefault();
        redo();
        return;
      }
      if (!selected) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        handleDeleteSelected();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        handleMoveSelected(-1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        handleMoveSelected(1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        openExactEntryForSelected();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    selected,
    readOnly,
    handleDeleteSelected,
    handleMoveSelected,
    undo,
    redo,
    openExactEntryForSelected,
  ]);

  // Les dialogues D8b/D18 vivent en flux normal APRÈS la grille : sur une
  // fenêtre visible étendue (plusieurs jours, vue élargie), ils peuvent
  // apparaître bien après le bas de l'écran sans qu'aucun scroll automatique
  // ne les révèle — l'utilisateur clique un bouton, "rien ne semble se
  // passer" alors que le dialogue attend son action hors champ.
  useEffect(() => {
    if (pendingStep !== null)
      stepConfirmRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [pendingStep]);
  useEffect(() => {
    if (applyAllConfirm !== null)
      applyAllConfirmRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [applyAllConfirm]);

  const activeDate = selectedDates[isNarrow ? mobileIndex : 0];

  return (
    <div className={className} data-testid="schedule-v2-editor" data-flag="v2">
      {readOnly && (
        <div
          data-testid="schedule-v2-readonly-banner"
          className="mb-2 rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning"
        >
          {t("schedule_v2.read_only_has_responses")}
        </div>
      )}

      {/* Barre d'outils : annuler/rétablir, pas, fenêtre visible, recopie entre jours. */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {!readOnly && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="schedule-v2-undo"
              disabled={past.length === 0}
              onClick={undo}
              aria-label={t("schedule_v2.undo_action")}
              title={t("schedule_v2.undo_action")}
              className="p-1.5 rounded border border-border hover:border-primary/50 disabled:opacity-30"
            >
              <Undo2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              data-testid="schedule-v2-redo"
              disabled={future.length === 0}
              onClick={redo}
              aria-label={t("schedule_v2.redo_action")}
              title={t("schedule_v2.redo_action")}
              className="p-1.5 rounded border border-border hover:border-primary/50 disabled:opacity-30"
            >
              <Redo2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              data-testid="schedule-v2-add-range"
              onClick={openExactEntryForCreate}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary/50"
            >
              <Plus className="w-3.5 h-3.5" />{" "}
              {t("schedule_v2.add_range_action")}
            </button>
          </div>
        )}
        <div
          className="flex items-center gap-1"
          role="group"
          aria-label={t("schedule_v2.toolbar_step_label")}
        >
          {STEP_VALUES.map((value) => (
            <button
              key={value}
              type="button"
              data-testid={`schedule-v2-step-${value}`}
              disabled={readOnly}
              onClick={() => requestStepChange(value as Step)}
              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                step === value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:border-primary/50"
              }`}
            >
              {value < 60 ? `${value} min` : `${value / 60} h`}
            </button>
          ))}
        </div>

        <button
          type="button"
          data-testid="schedule-v2-show-earlier"
          disabled={readOnly || visibleStart <= 0}
          className="text-xs px-2 py-1 rounded border border-border hover:border-primary/50 disabled:opacity-40"
          onClick={() => setVisibleStart((v) => Math.max(0, v - 120))}
        >
          {t("schedule_v2.toolbar_show_earlier")}
        </button>
        <button
          type="button"
          data-testid="schedule-v2-show-later"
          disabled={readOnly || visibleEnd >= DAY_MINUTES}
          className="text-xs px-2 py-1 rounded border border-border hover:border-primary/50 disabled:opacity-40"
          onClick={() => setVisibleEnd((v) => Math.min(DAY_MINUTES, v + 120))}
        >
          {t("schedule_v2.toolbar_show_later")}
        </button>
        <button
          type="button"
          data-testid="schedule-v2-show-full-day"
          disabled={readOnly}
          className="text-xs px-2 py-1 rounded border border-border hover:border-primary/50 disabled:opacity-40"
          onClick={() => {
            setVisibleStart(0);
            setVisibleEnd(DAY_MINUTES);
          }}
        >
          {t("schedule_v2.toolbar_show_full_day")}
        </button>

        {selectedDates.length > 1 && (
          <div className="ml-auto flex items-center gap-1">
            <CopyPlus
              className="w-3.5 h-3.5 text-muted-foreground"
              aria-hidden="true"
            />
            <button
              type="button"
              data-testid="schedule-v2-apply-all"
              disabled={readOnly}
              className="text-xs px-2 py-1 rounded border border-border hover:border-primary/50"
              onClick={() => requestApplyToAllDays("all")}
            >
              {t("schedule_v2.apply_to_all_days_target_all")}
            </button>
            <button
              type="button"
              data-testid="schedule-v2-apply-weekdays"
              disabled={readOnly}
              className="text-xs px-2 py-1 rounded border border-border hover:border-primary/50"
              onClick={() => requestApplyToAllDays("weekdays")}
            >
              {t("schedule_v2.apply_to_all_days_target_weekdays")}
            </button>
          </div>
        )}
      </div>

      {/* Pager mobile (D24). */}
      {isNarrow && activeDate && (
        <div className="mb-2 flex items-center justify-between gap-2">
          <button
            type="button"
            data-testid="schedule-v2-day-prev"
            disabled={mobileIndex === 0}
            className="p-1 rounded border border-border disabled:opacity-30"
            onClick={() => setMobileIndex((i) => Math.max(0, i - 1))}
            aria-label={t("schedule_v2.toolbar_show_earlier")}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span
            data-testid="schedule-v2-day-current"
            className="text-sm font-medium"
          >
            {activeDate}
          </span>
          <button
            type="button"
            data-testid="schedule-v2-day-next"
            disabled={mobileIndex >= selectedDates.length - 1}
            className="p-1 rounded border border-border disabled:opacity-30"
            onClick={() =>
              setMobileIndex((i) => Math.min(selectedDates.length - 1, i + 1))
            }
            aria-label={t("schedule_v2.toolbar_show_later")}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <div style={{ height: 420 }}>
        <ScheduleGridV2
          ref={gridRef}
          dates={datesToRender}
          dayRanges={dayRanges}
          step={step}
          visibleStartMinute={visibleStart}
          visibleEndMinute={visibleEnd}
          selected={selected}
          onSelect={setSelected}
          onRangesChange={setDayRangesFor}
          onMultiColumnPaint={handleMultiColumnPaint}
          readOnly={readOnly}
          todayCutoff={todayCutoff}
        />
      </div>

      {/* D13 : jours sautés par le dernier glisser multi-colonnes (chevauchement). */}
      {skippedDaysNotice && skippedDaysNotice.length > 0 && (
        <div
          data-testid="schedule-v2-multi-column-skip-notice"
          role="status"
          className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-sm"
        >
          <span>
            {t("schedule_v2.multi_column_skip", {
              count: skippedDaysNotice.length,
              days: skippedDaysNotice
                .map((d) => dayFormatter.format(new Date(d + "T00:00:00")))
                .join(", "),
            })}
          </span>
          <button
            type="button"
            data-testid="schedule-v2-multi-column-skip-dismiss"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setSkippedDaysNotice(null)}
            aria-label={t("common:close")}
          >
            ×
          </button>
        </div>
      )}

      {/* D21 : une nouvelle proposition IA a été reçue APRÈS l'hydratation
          initiale pour un jour que l'utilisateur avait déjà retouché à la
          main — jamais écrasé silencieusement, un badge par jour propose
          d'appliquer ou d'ignorer, plus "tout appliquer" à partir de 2 jours. */}
      {!readOnly &&
        Object.entries(pendingAiProposals).map(([date, proposed]) => (
          <div
            key={date}
            data-testid={`schedule-v2-ai-proposal-${date}`}
            role="status"
            className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm"
          >
            <span>
              {t("schedule_v2.ai_proposal_badge", {
                day: dayFormatter.format(new Date(date + "T00:00:00")),
                range:
                  proposed.length > 0
                    ? proposed.map(formatRangeShort).join(", ")
                    : t("schedule_v2.all_day_label"),
              })}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                data-testid={`schedule-v2-ai-proposal-apply-${date}`}
                className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground"
                onClick={() => applyAiProposal(date)}
              >
                {t("schedule_v2.ai_proposal_apply")}
              </button>
              <button
                type="button"
                data-testid={`schedule-v2-ai-proposal-dismiss-${date}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => dismissAiProposal(date)}
                aria-label={t("common:close")}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      {!readOnly && Object.keys(pendingAiProposals).length > 1 && (
        <button
          type="button"
          data-testid="schedule-v2-ai-proposal-apply-all"
          className="mt-1 text-xs px-2 py-1 rounded border border-primary/40 text-primary hover:bg-primary/5"
          onClick={applyAllAiProposals}
        >
          {t("schedule_v2.ai_proposal_apply_all")}
        </button>
      )}

      {/* D17/D26 : saisie exacte — popover sur desktop, bottom sheet sur
          mobile (D24). Ouverte par Entrée sur une plage sélectionnée
          (édition) ou par le bouton "Ajouter un créneau" (création,
          entièrement au clavier). */}
      {exactEntry && !readOnly && (
        <div
          data-testid="schedule-v2-exact-entry"
          role="dialog"
          aria-modal="true"
          aria-label={
            exactEntry.mode === "create"
              ? t("schedule_v2.exact_entry_create_title")
              : t("schedule_v2.exact_entry_edit_title")
          }
          className={
            isNarrow
              ? "fixed inset-x-0 bottom-0 z-50 rounded-t-xl border-t border-border bg-card p-4 shadow-lg"
              : "mt-2 max-w-xs rounded-lg border border-primary/40 bg-card p-3 shadow-lg"
          }
        >
          <p className="text-sm font-medium mb-2">
            {exactEntry.mode === "create"
              ? t("schedule_v2.exact_entry_create_title")
              : t("schedule_v2.exact_entry_edit_title")}
          </p>
          {exactEntry.mode === "create" && selectedDates.length > 1 && (
            <select
              data-testid="schedule-v2-exact-entry-date"
              className="mb-2 w-full text-sm border border-border rounded px-2 py-1"
              value={exactEntry.date}
              onChange={(e) =>
                setExactEntry((prev) =>
                  prev ? { ...prev, date: e.target.value } : prev,
                )
              }
            >
              {selectedDates.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <label
              className="text-xs text-muted-foreground"
              htmlFor="schedule-v2-exact-start"
            >
              {t("schedule_v2.exact_entry_start_label")}
            </label>
            <input
              id="schedule-v2-exact-start"
              data-testid="schedule-v2-exact-entry-start"
              type="time"
              step={900}
              value={minutesToTimeInput(exactEntry.start)}
              onChange={(e) => {
                const parsed = parseTimeInput(e.target.value);
                if (parsed === null) return;
                setExactEntry((prev) =>
                  prev ? { ...prev, start: parsed } : prev,
                );
              }}
              className="text-sm border border-border rounded px-2 py-1"
            />
            <span className="text-xs text-muted-foreground">–</span>
            <label
              className="text-xs text-muted-foreground"
              htmlFor="schedule-v2-exact-end"
            >
              {t("schedule_v2.exact_entry_end_label")}
            </label>
            <input
              id="schedule-v2-exact-end"
              data-testid="schedule-v2-exact-entry-end"
              type="time"
              step={900}
              value={minutesToTimeInput(exactEntry.end)}
              onChange={(e) => {
                const parsed = parseTimeInput(e.target.value);
                if (parsed === null) return;
                setExactEntry((prev) =>
                  prev ? { ...prev, end: parsed } : prev,
                );
              }}
              className="text-sm border border-border rounded px-2 py-1"
            />
          </div>
          {exactEntryError && (
            <p
              data-testid="schedule-v2-exact-entry-error"
              className="text-xs text-destructive mb-2"
            >
              {t(`schedule_v2.exact_entry_error_${exactEntryError}`)}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="schedule-v2-exact-entry-submit"
              className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground"
              onClick={submitExactEntry}
            >
              {exactEntry.mode === "create"
                ? t("schedule_v2.exact_entry_create_action")
                : t("schedule_v2.exact_entry_save_action")}
            </button>
            <button
              type="button"
              data-testid="schedule-v2-exact-entry-cancel"
              className="text-xs px-2 py-1 rounded border border-border"
              onClick={() => {
                setExactEntry(null);
                setExactEntryError(null);
              }}
            >
              {t("common:cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Barre d'actions sur la plage sélectionnée (D11, D1b, D26). */}
      {selected && selectedRange && !readOnly && (
        <div
          data-testid="schedule-v2-selection-toolbar"
          className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2"
        >
          <span className="text-sm font-medium">
            {selected.date} ·{" "}
            {String(Math.floor(selectedRange.start / 60)).padStart(2, "0")}:
            {String(selectedRange.start % 60).padStart(2, "0")} –{" "}
            {String(Math.floor(selectedRange.end / 60)).padStart(2, "0")}:
            {String(selectedRange.end % 60).padStart(2, "0")}
          </span>
          <button
            type="button"
            data-testid="schedule-v2-delete-range"
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-destructive/40 text-destructive hover:bg-destructive/10"
            onClick={handleDeleteSelected}
          >
            <Trash2 className="w-3.5 h-3.5" />{" "}
            {t("schedule_v2.range_delete_action")}
          </button>
          {canMerge && (
            <button
              type="button"
              data-testid="schedule-v2-merge-range"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary/50"
              onClick={handleMerge}
            >
              <Link2 className="w-3.5 h-3.5" />{" "}
              {t("schedule_v2.range_merge_action")}
            </button>
          )}
          {selectedRange.end - selectedRange.start > 15 && (
            <button
              type="button"
              data-testid="schedule-v2-split-toggle"
              aria-pressed={splitMode}
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${
                splitMode
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50"
              }`}
              onClick={() => setSplitMode((v) => !v)}
            >
              <Scissors className="w-3.5 h-3.5" />{" "}
              {t("schedule_v2.range_split_action")}
            </button>
          )}
          {splitMode && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">
                {t("schedule_v2.range_split_hint")}
              </span>
              <select
                data-testid="schedule-v2-split-select"
                className="text-xs border border-border rounded px-1 py-0.5"
                onChange={(e) => handleSplitAt(Number(e.target.value))}
                defaultValue=""
              >
                <option value="" disabled>
                  --:--
                </option>
                {Array.from(
                  {
                    length:
                      Math.floor(
                        (selectedRange.end - selectedRange.start) / 15,
                      ) - 1,
                  },
                  (_, i) => selectedRange.start + (i + 1) * 15,
                ).map((m) => (
                  <option key={m} value={m}>
                    {String(Math.floor(m / 60)).padStart(2, "0")}:
                    {String(m % 60).padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Dialogue D8b : changement de pas qui modifierait des plages. */}
      {pendingStep !== null && (
        <div
          ref={stepConfirmRef}
          data-testid="schedule-v2-step-confirm-dialog"
          role="alertdialog"
          aria-modal="true"
          className="mt-2 rounded-lg border border-warning/50 bg-warning/10 p-3"
        >
          <p className="text-sm mb-2">{t("schedule_v2.realign_dialog_body")}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="schedule-v2-step-confirm-apply"
              className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground"
              onClick={() => applyPendingStep("realign")}
            >
              {t("schedule_v2.realign_dialog_apply")}
            </button>
            <button
              type="button"
              data-testid="schedule-v2-step-confirm-keep"
              className="text-xs px-2 py-1 rounded border border-border"
              onClick={() => applyPendingStep("keep")}
            >
              {t("schedule_v2.realign_dialog_keep")}
            </button>
            <button
              type="button"
              data-testid="schedule-v2-step-confirm-display-only"
              className="text-xs px-2 py-1 rounded border border-border"
              onClick={() => applyPendingStep("display-only")}
            >
              {t("schedule_v2.realign_dialog_display_only")}
            </button>
          </div>
        </div>
      )}

      {/* Confirmation D18 : la recopie remplacerait des horaires différents. */}
      {applyAllConfirm && (
        <div
          ref={applyAllConfirmRef}
          data-testid="schedule-v2-apply-all-confirm-dialog"
          role="alertdialog"
          aria-modal="true"
          className="mt-2 rounded-lg border border-warning/50 bg-warning/10 p-3"
        >
          <p className="text-sm mb-2">
            {t("schedule_v2.apply_to_all_days_confirm_body", {
              count: applyAllConfirm.affected.length,
            })}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="schedule-v2-apply-all-confirm-apply"
              className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground"
              onClick={confirmApplyToAllDays}
            >
              {t("schedule_v2.apply_to_all_days_confirm_apply")}
            </button>
            <button
              type="button"
              data-testid="schedule-v2-apply-all-confirm-cancel"
              className="text-xs px-2 py-1 rounded border border-border"
              onClick={() => setApplyAllConfirm(null)}
            >
              {t("schedule_v2.apply_to_all_days_confirm_cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
